import React, { useState, useRef, useEffect } from "react";
import {
  Row,
  Col,
  Spinner,
  Button,
  Table,
  Toast,
  ToastContainer,
} from "react-bootstrap";
import Loader from "../common/Loader.jsx";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { FaCalendarAlt, FaUpload } from "react-icons/fa";

import Back from "../../assets/svg/back.svg";
import SearchWithAPI from "../common/searchbox";
import PaymentInvoiceModal from "./payment-invoice-modal";
import "./payment.css";
import Calendar from "../../assets/svg/calendar1.svg";
import DragDrop from "../../assets/svg/dragdrop.svg";
import Customize from "../../assets/svg/customize.svg";
import { ReactComponent as HistoryIc } from "../../assets/svg/history_icv.svg";
import * as paymentApi from "./payment-api";
import { sanitizeInput, sanitizeNumericInput } from "../common/security-utils";

// Invoice table columns that can be shown/hidden (the checkbox column is
// always shown). Persisted to sessionStorage so the choice survives a
// refresh, but clears when the browser tab is closed.
const INVOICE_COLUMN_OPTIONS = [
  "Invoice No.",
  "Invoice Date",
  "Without Tax",
  "Total",
  "Received",
  "Outstanding",
  "Currency Value",
  "TDS",
  "TCS",
  "Payment Amount",
  "Difference",
];
const INVOICE_COLUMNS_STATE_KEY = "paymentCreateInvoiceColumns";

const loadPersistedColumns = () => {
  try {
    const raw = sessionStorage.getItem(INVOICE_COLUMNS_STATE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed && typeof parsed === "object") {
      // Merge over the defaults so a newly-added column defaults to visible
      // even if an older persisted selection doesn't mention it yet.
      return {
        ...Object.fromEntries(INVOICE_COLUMN_OPTIONS.map((label) => [label, true])),
        ...parsed,
      };
    }
  } catch {
    // ignore malformed storage — fall through to defaults
  }
  return Object.fromEntries(INVOICE_COLUMN_OPTIONS.map((label) => [label, true]));
};

const ErrorMessage = ({ message }) => {
  if (!message) return null;
  return (
    <span
      style={{
        color: "#ff4444",
        fontSize: "12px",
        fontWeight: "400",
        display: "block",
        marginTop: "4px",
      }}
    >
      {message}
    </span>
  );
};

const PaymentCreate = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = location.state || {};

  const fileInputRef = useRef(null);
  const paymentDateInputRef = useRef(null);

  const [paymentId, setPaymentId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Decode an HTML entity symbol (e.g. "&#8377;" -> "₹") to a plain character.
  const decodeHtmlEntity = (str) => {
    if (!str) return "";
    const el = document.createElement("textarea");
    el.innerHTML = str;
    return el.value;
  };

  // Organization's own currency (from the backend, via organizations.currency_id)
  // — used as the "converted" currency symbol next to each invoice-currency amount.
  const [orgCurrencySymbol, setOrgCurrencySymbol] = useState("");

  // State management
  const [paymentDate, setPaymentDate] = useState(new Date());
  const [customerOption, setCustomerOption] = useState(null);
  const [globalPaymentAmount, setGlobalPaymentAmount] = useState("");
  // When on, the customer's unallocated balance (from prior payments) is
  // added on top of the typed Payment Amount for allocation purposes — the
  // input itself still only shows what was typed.
  const [includeUnallocated, setIncludeUnallocated] = useState(false);
  const [globalTdsPercent, setGlobalTdsPercent] = useState("");
  const [currencyRate, setCurrencyRate] = useState("");
  const [paymentType, setPaymentType] = useState(null); // Will store {value, label} or string
  const [paymentTypes, setPaymentTypes] = useState([]); // Will store [{value, label}, ...]
  const [remarks, setRemarks] = useState("");
  const [chequeNumber, setChequeNumber] = useState("");
  const [chequeDate, setChequeDate] = useState("");
  const [accountNo, setAccountNo] = useState("");
  const [accountName, setAccountName] = useState("");
  const [netBankingId, setNetBankingId] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  // Filters the invoice table down to a single currency (dropdown built from
  // the loaded customer's own invoices — not all system currencies).
  const [currencyFilter, setCurrencyFilter] = useState("");
  // Filters the invoice table by invoice number (Search box).
  const [invoiceSearchTerm, setInvoiceSearchTerm] = useState("");
  // Which invoice-table columns are shown — persisted so it survives a
  // refresh (cleared only when the tab closes).
  const [visibleColumns, setVisibleColumns] = useState(loadPersistedColumns);
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [loadingPaymentTypes, setLoadingPaymentTypes] = useState(false);

  // Paid history modal (per-invoice), shown from the Received column.
  const [showPaidHistory, setShowPaidHistory] = useState(false);
  const [paidHistory, setPaidHistory] = useState([]);
  const [isPaidHistoryLoading, setIsPaidHistoryLoading] = useState(false);
  const [paidHistoryInvoice, setPaidHistoryInvoice] = useState(null);

  // Customer data
  const [customerData, setCustomerData] = useState({
    customerName: "",
    revenue: 0,
    totalInvoices: 0,
    totalPayment: 0,
    amountReceived: 0,
    outstanding: 0,
    unallocatedBalance: 0,
  });

  // Sample invoices data (would come from API)
  const [invoices, setInvoices] = useState([]);

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarStatus, setSnackbarStatus] = useState(false);
  const handleSnackbarShow = () => setShowSnackbar(true);
  const handleSnackbarClose = () => setShowSnackbar(false);

  const [validations, setValidations] = useState({});

  const sanitize = (value) => DOMPurify.sanitize(value ?? "");

  // Sanitize + coerce to a number, defaulting to 0. Used everywhere an
  // invoice/amount field needs to be treated as a plain number.
  const toNum = (value) => Number(sanitizeNumericInput(value || 0)) || 0;

  // Payment Amount actually used for allocation/validation/submission — the
  // typed amount plus the customer's unallocated balance when the "Include
  // Unallocated Amt" switch is on. The input itself always shows only what
  // was typed; this derived value is what drives the waterfall.
  const effectivePaymentAmount =
    toNum(globalPaymentAmount) +
    (includeUnallocated ? toNum(customerData.unallocatedBalance) : 0);

  // Show a snackbar message with a success/error status in one call.
  const notify = (message, isSuccess) => {
    setSnackbarMessage(message);
    setSnackbarStatus(isSuccess);
    handleSnackbarShow();
  };

  // How much can still be paid on an invoice, given what's already paid and
  // its TDS amount. A row is "fully settled" once paid + TDS covers the
  // total — in that case the cap is 0 and no further payment is allowed.
  const getInvoiceCap = (inv) => {
    const totalAmount = toNum(inv?.totalAmount);
    const paidAmount = toNum(inv?.paidAmount);
    const tdsAmt = toNum(inv?.tdsAmt);
    return {
      cap: Math.max(totalAmount - paidAmount - tdsAmt, 0),
      isFullySettled: paidAmount + tdsAmt >= totalAmount,
    };
  };

  // Handlers
  const handleCustomerDropdownChange = async (option) => {
    setCustomerOption(option);
    setValidations((prev) => ({ ...prev, customer_id: null }));
    setCurrencyFilter("");
    setInvoiceSearchTerm("");
    if (!option?.value) {
      setInvoices([]);
      setCustomerData({
        customerName: "",
        revenue: 0,
        totalInvoices: 0,
        totalPayment: 0,
        amountReceived: 0,
        outstanding: 0,
        unallocatedBalance: 0,
      });
      return;
    }

    try {
      const response = await paymentApi.getCustomerPaymentDetailsApi(option.value);
      const body = response?.data;

      if (body?.success || body?.invoices) {
        const summary = body.summary || {};
        setCustomerData({
          customerName: summary.customer || option.label || "—",
          revenue: Number(summary.annual_revenue || 0),
          totalInvoices: Number(summary.invoice_count || 0),
          totalPayment: Number(summary.total_amount || 0),
          amountReceived: Number(summary.paid_amount || 0),
          outstanding: Number(summary.pending_amount || 0),
          // Unallocated amount from prior payments (Payment Amount entered
          // beyond what was allocated to invoices), carried forward against
          // this customer — available to apply toward future invoices.
          unallocatedBalance: Number(summary.unallocated_balance || 0),
        });
        setOrgCurrencySymbol(decodeHtmlEntity(summary.org_currency_symbol) || "");

        const invoiceList = body.invoices || body.invoice || body.data || [];
        if (Array.isArray(invoiceList)) {
          const formatted = invoiceList.map((inv, idx) => {
            const totalAmountFc = sanitizeNumericInput(
              inv.total_amount_fc !== undefined && inv.total_amount_fc !== null
                ? inv.total_amount_fc
                : inv.total_amount || inv.amount || 0
            );
            const totalAmountBc = sanitizeNumericInput(inv.total_amount_bc || 0);
            const pendingAmountFc = sanitizeNumericInput(
              inv.pending_amount_fc !== undefined && inv.pending_amount_fc !== null
                ? inv.pending_amount_fc
                : inv.pending_amount || inv.outstanding || 0
            );
            const pendingAmountBc = sanitizeNumericInput(inv.pending_amount_bc || 0);
            const paidAmountFc = sanitizeNumericInput(
              inv.paid_amount_fc !== undefined && inv.paid_amount_fc !== null
                ? inv.paid_amount_fc
                : inv.paid_amount || 0
            );
            const paidAmountBc = sanitizeNumericInput(inv.paid_amount_bc || 0);
            const withoutTaxFc = sanitizeNumericInput(
              inv.sub_total_fc !== undefined && inv.sub_total_fc !== null
                ? inv.sub_total_fc
                : inv.sub_total || 0
            );
            const withoutTaxBc = sanitizeNumericInput(inv.sub_total_bc || 0);

            return {
              id: inv.id,
              // Guaranteed-unique row identity for React keys/selection state —
              // some records come back with duplicate/missing API ids, which
              // previously caused row updates to collide (only the last row
              // with a shared id would ever visibly update).
              rowKey: `${inv.id ?? "row"}-${idx}`,
              invoiceNo: sanitizeInput(inv.invoice_number || inv.invoice || `#${idx}`),
              invoiceDate: sanitizeInput(inv.invoice_date || ""),
              withoutTax: withoutTaxFc,
              withoutTaxBc: withoutTaxBc,
              totalAmount: totalAmountFc,
              totalAmountFc: totalAmountFc,
              totalAmountBc: totalAmountBc,
              paidAmount: paidAmountFc,
              paidAmountFc: paidAmountFc,
              paidAmountBc: paidAmountBc,
              outstanding: pendingAmountFc,
              pendingAmountFc: pendingAmountFc,
              pendingAmountBc: pendingAmountBc,
              currencyRate: sanitizeNumericInput(inv.exchange_rate || 0),
              currencySymbol: inv.currency_symbol || "",
              currencyName: inv.currency_name || "",
              currencyId: inv.currency_id ?? inv.currency_symbol ?? "",
              paymentAmt: "",
              paymentAmtOrg: "",
              // Editable per-invoice TDS (seeded from API if present).
              tdsAmt: sanitizeNumericInput(inv.tds_amount_bc || inv.tds_amount || 0) || "",
            };
          });
          setInvoices(formatted);
          notify(`Loaded ${formatted.length} invoices`, true);
        }
      } else {
        setInvoices([]);
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      notify("Error loading invoices for this customer", false);
    }
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    const maxSize = 5 * 1024 * 1024; // 5MB
    const validFiles = [];
    const invalidFiles = [];

    for (const file of files) {
      if (file.size > maxSize) {
        invalidFiles.push(file.name);
      } else {
        try {
          const base64 = await fileToBase64(file);
          validFiles.push({
            file: file,
            name: sanitizeInput(file.name),
            size: file.size,
            type: file.type,
            base64: base64,
          });
        } catch (error) {
          console.error("Error converting file to base64:", error);
          invalidFiles.push(file.name);
        }
      }
    }

    if (invalidFiles.length > 0) {
      notify(`File(s) too large or invalid: ${invalidFiles.join(", ")}. Max size is 5MB.`, false);
    }

    if (validFiles.length > 0) {
      setUploadedFiles((prev) => [...prev, ...validFiles]);
      notify(`${validFiles.length} file(s) uploaded successfully`, true);
    }

    // Reset file input
    e.target.value = "";
  };

  const removeFile = (index) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Open the paid-history modal for an invoice's Received amount and fetch
  // its payment timeline — same API/design as invoice-view.jsx's Paid History.
  const openPaidHistory = async (invoice) => {
    setPaidHistoryInvoice(invoice);
    setShowPaidHistory(true);
    setPaidHistory([]);
    setIsPaidHistoryLoading(true);
    try {
      const response = await paymentApi.getInvoicePaymentHistoryApi(invoice.id);
      const data = response?.data ?? response;
      const historyList = data?.history ?? data?.payments ?? data?.data ?? [];
      setPaidHistory(Array.isArray(historyList) ? historyList : []);
    } catch (err) {
      notify("Failed to fetch payment history", false);
    } finally {
      setIsPaidHistoryLoading(false);
    }
  };

  const closePaidHistory = () => {
    setShowPaidHistory(false);
    setPaidHistoryInvoice(null);
    setPaidHistory([]);
  };

  // Currency of the current selection (taken from the first selected invoice).
  // Once an invoice is selected, only same-currency invoices may be selected.
  const selectedCurrency = (() => {
    if (selectedInvoices.length === 0) return null;
    const firstSelected = invoices.find((inv) => inv.rowKey === selectedInvoices[0]);
    return firstSelected?.currencyId ?? null;
  })();

  // Decoded symbol of the currently-selected invoice(s)' currency (e.g. "₹"),
  // for comparing against the organization's own currency symbol below.
  const selectedCurrencySymbol = (() => {
    if (selectedInvoices.length === 0) return null;
    const firstSelected = invoices.find((inv) => inv.rowKey === selectedInvoices[0]);
    return firstSelected?.currencySymbol ? decodeHtmlEntity(firstSelected.currencySymbol) : null;
  })();

  // Distinct currencies actually present among the loaded customer's
  // invoices — powers the Currency filter dropdown (not all system
  // currencies, just the ones this customer is actually invoiced in).
  const distinctInvoiceCurrencies = Array.from(
    new Map(
      invoices
        .filter((inv) => inv.currencyId)
        .map((inv) => {
          const symbol = decodeHtmlEntity(inv.currencySymbol) || inv.currencyId;
          const name = decodeHtmlEntity(inv.currencyName);
          const label = name ? `${symbol} - ${name}` : symbol;
          return [inv.currencyId, label];
        }),
    ).entries(),
  );

  // Invoices shown in the table, narrowed to the selected currency filter
  // and the Search box (matched against the invoice number).
  const filteredInvoices = invoices
    .filter((inv) => !currencyFilter || String(inv.currencyId) === String(currencyFilter))
    .filter((inv) =>
      !invoiceSearchTerm ||
      inv.invoiceNo?.toLowerCase().includes(invoiceSearchTerm.trim().toLowerCase()),
    );

  // Currency Rate is only relevant (and shown) once an invoice is selected
  // whose currency symbol differs from the organization's own currency
  // symbol. Re-derived on every render, so it naturally re-validates on
  // every invoice check/uncheck (selectedInvoices change).
  const showCurrencyRate =
    orgCurrencySymbol &&
    selectedCurrencySymbol &&
    orgCurrencySymbol !== selectedCurrencySymbol;

  // Effective conversion rate for a given invoice: prefer the manually
  // typed global Currency Rate field; if it's empty, fall back to the
  // invoice's own stored exchange_rate (currencyRate) — but only when the
  // organization's currency actually differs from that invoice's currency,
  // since same-currency invoices have no meaningful rate to fall back to.
  const getEffectiveRate = (inv) => {
    const typed = toNum(currencyRate);
    if (typed > 0) return typed;

    const invSymbol = decodeHtmlEntity(inv?.currencySymbol);
    if (orgCurrencySymbol && invSymbol && orgCurrencySymbol !== invSymbol) {
      const invRate = toNum(inv?.currencyRate);
      if (invRate > 0) return invRate;
    }

    return 0;
  };

  // An invoice is selectable when nothing is selected yet, or its currency
  // matches the active selection currency.
  const isInvoiceSelectable = (invoice) =>
    selectedCurrency === null || invoice.currencyId === selectedCurrency;

  // Count of invoices that can be selected under the active (or default)
  // currency — used for the header "select all" checkbox state.
  const selectableCount = (() => {
    const targetCurrency = selectedCurrency ?? invoices[0]?.currencyId ?? null;
    return invoices.filter((inv) => inv.currencyId === targetCurrency).length;
  })();

  // Apply the current global TDS % to a single invoice — shared by
  // handleGlobalTdsPercentChange (when TDS % changes) and handleInvoiceSelect
  // (when a row is selected AFTER the global TDS %/Payment Amount were
  // already entered, so it doesn't miss the calculation). pctOverride lets
  // the TDS-change handler pass the just-typed value before state commits.
  // Only tdsAmt is set here — the actual payable is computed by the
  // auto-distribute effect's waterfall (first selected row filled to its
  // full outstanding-minus-TDS cap, remainder spills to the next row).
  const applyGlobalTdsToInvoice = (inv, pctOverride) => {
    const pct = pctOverride ?? toNum(globalTdsPercent);
    if (pct <= 0) return inv;

    const withoutTax = toNum(inv.withoutTax);
    const tdsAmt = +(withoutTax * (pct / 100)).toFixed(2);

    return {
      ...inv,
      tdsAmt: tdsAmt ? String(tdsAmt) : "",
    };
  };

  const handleInvoiceSelect = (rowKey) => {
    const invoice = invoices.find((inv) => inv.rowKey === rowKey);
    const isSelected = selectedInvoices.includes(rowKey);

    // Block selecting an invoice of a different currency than the active one.
    if (!isSelected && invoice && !isInvoiceSelectable(invoice)) {
      notify("You can only select invoices of the same currency.", false);
      return;
    }

    if (!isSelected) {
      // Selecting a row: remember whatever TDS was already on it (e.g. a
      // manually-typed value) before the global TDS % overwrites it below,
      // so it can be restored if the row is later deselected.
      setInvoices((prev) =>
        prev.map((inv) =>
          inv.rowKey === rowKey
            ? applyGlobalTdsToInvoice({ ...inv, prevTdsAmt: inv.tdsAmt }, undefined)
            : inv,
        ),
      );
    } else {
      // Deselecting: restore the TDS value that was there before it got
      // overwritten (e.g. by the global TDS % on select), instead of
      // leaving whatever the global-%-driven calculation left behind.
      setInvoices((prev) =>
        prev.map((inv) =>
          inv.rowKey === rowKey
            ? { ...inv, tdsAmt: inv.prevTdsAmt ?? inv.tdsAmt, prevTdsAmt: undefined }
            : inv,
        ),
      );
    }

    setSelectedInvoices((prev) =>
      prev.includes(rowKey)
        ? prev.filter((k) => k !== rowKey)
        : [...prev, rowKey],
    );
    setValidations((prev) => ({ ...prev, invoices: null }));
  };

  // Editable per-invoice TDS amount. The input is typed/shown in
  // ORGANIZATION currency (orgValue), then converted here to this invoice's
  // own currency via the global Currency Rate before being stored in
  // tdsAmt — the same unit getInvoiceCap/the backend payload expect.
  // Changing it can shrink (or zero out) this row's payment cap, so
  // re-clamp/clear the existing Payment Amount against the new cap instead
  // of leaving a stale value on screen.
  const handleTdsAmtChange = (rowKey, orgValue) => {
    setInvoices((prev) =>
      prev.map((inv) => {
        if (inv.rowKey !== rowKey) return inv;

        const rate = getEffectiveRate(inv) || 1;
        const value = orgValue === "" ? "" : String(toNum(orgValue) / rate);

        const { cap, isFullySettled } = getInvoiceCap({ ...inv, tdsAmt: value });

        if (isFullySettled) {
          if (toNum(inv.paidAmount) + toNum(value) > toNum(inv.totalAmount)) {
            notify("TDS + Paid exceeds the invoice total — Payment Amount cleared", false);
          }
          return { ...inv, tdsAmt: value, paymentAmt: "", paymentAmtOrg: "", userEdited: false, tdsLocked: false };
        }

        if (toNum(inv.paymentAmt) > cap) {
          notify(`Payment Amount reduced to ${formatCurrency(cap)} to fit the new TDS`, false);
          return { ...inv, tdsAmt: value, paymentAmt: String(cap), paymentAmtOrg: "" };
        }

        return { ...inv, tdsAmt: value };
      }),
    );
  };

  // Global TDS % — applied to every currently-selected invoice's "without
  // tax" amount to compute that row's TDS. If a global Payment Amount is
  // already set, the row's payable is also updated to (outstanding - TDS).
  // If the global Payment Amount is empty, only the TDS field is filled —
  // the payable is left for the Payment Amount flow to calculate.
  const handleGlobalTdsPercentChange = (value) => {
    const sanitized = sanitizeNumericInput(value);
    setGlobalTdsPercent(sanitized);

    const pct = Number(sanitized) || 0;

    setInvoices((prev) =>
      prev.map((inv) => {
        if (!selectedInvoices.includes(inv.rowKey)) return inv;

        if (pct <= 0) {
          // TDS % cleared — reset TDS/lock on this row.
          return { ...inv, tdsAmt: "", tdsLocked: false };
        }

        return applyGlobalTdsToInvoice(inv, pct);
      }),
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // Select all invoices that share a single currency. Use the currency of
      // the already-active selection, or the first invoice's currency.
      const targetCurrency =
        selectedCurrency ?? invoices[0]?.currencyId ?? null;
      setSelectedInvoices(
        invoices
          .filter((invoice) => invoice.currencyId === targetCurrency)
          .map((invoice) => invoice.rowKey),
      );
    } else {
      // Deselect all invoices
      setSelectedInvoices([]);
    }
  };

  const handleCloseModal = () => {
    setShowInvoiceModal(false);
    setSelectedInvoice(null);
  };

  const toggleColumn = (label) => {
    setVisibleColumns((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  // Persist column visibility so it survives a refresh.
  useEffect(() => {
    try {
      sessionStorage.setItem(INVOICE_COLUMNS_STATE_KEY, JSON.stringify(visibleColumns));
    } catch {
      // ignore storage write errors (e.g. private mode quota)
    }
  }, [visibleColumns]);

  // Reset a row's payable back to empty/unlocked. By default also clears
  // userEdited (used when the row is no longer selected or the global amount
  // was cleared); pass keepUserEdited when a manual override should survive.
  const resetRow = (inv, { keepUserEdited = false } = {}) => {
    const needsReset = inv.paymentAmt || inv.userEdited || inv.tdsLocked;
    if (!needsReset) return inv;
    return {
      ...inv,
      paymentAmt: "",
      paymentAmtOrg: "",
      tdsLocked: false,
      ...(keepUserEdited ? {} : { userEdited: false }),
    };
  };

  // Memoized so this string only rebuilds when the invoices' TDS amounts (for
  // currently-selected rows) actually change, not on every render.
  const selectedTdsKey = selectedInvoices
    .map((rowKey) => invoices.find((inv) => inv.rowKey === rowKey)?.tdsAmt || "")
    .join(",");

  // Auto-distribute payment amount across selected invoices
  // Mirrors PaymentForm.jsx: recalculate when payment amount or checkbox states change
  //
  // effectivePaymentAmount (typed amount + unallocated balance, if included)
  // is in the ORGANIZATION's currency, while each invoice's cap/paymentAmt
  // live in that invoice's own currency. The global Currency Rate field is
  // the conversion factor between the two, so the waterfall pool
  // (userTotal/remaining/caps) is run entirely in org currency and only
  // converted back to invoice currency for the final paymentAmt.
  useEffect(() => {
    const paymentAmt = effectivePaymentAmount;

    setInvoices((prev) => {
      const selected = prev.filter((inv) => selectedInvoices.includes(inv.rowKey));

      if (selected.length === 0) {
        return prev.map((inv) => (inv.userEdited ? inv : resetRow(inv)));
      }

      // All currently-selected invoices share one currency (enforced by
      // isInvoiceSelectable), so a single rate applies to the whole pool —
      // prefer the typed global rate, else fall back to the first selected
      // invoice's own stored rate.
      const rate = getEffectiveRate(selected[0]) || 1;

      // Global Payment Amount cleared: reset every row's payable (including
      // manually-typed and TDS%-driven ones) so a fresh amount entered later
      // recalculates cleanly instead of getting stuck on a stale lock.
      /* if (paymentAmt <= 0) {
        return prev.map((inv) => resetRow(inv));
      } */

      // Only manually-typed rows are truly fixed. TDS-locked rows keep their
      // recorded TDS but their payable is recomputed below in the same
      // waterfall pool as everything else — so the first selected row is
      // filled up to its full (outstanding - TDS) cap, then the remainder
      // spills to the next selected row, and so on, in table order.
      // userTotal is converted to org currency to compare against paymentAmt.
      const fixedRows = selected.filter((inv) => inv.userEdited);
      const userTotal = fixedRows.reduce((s, inv) => s + toNum(inv.paymentAmt) * rate, 0);

      // If manually-set payables alone already exceed the global amount,
      // scale them down proportionally so the total never exceeds what was
      // entered in Payment Amount. Everything else gets nothing left to
      // auto-fill, same as the normal remaining=0 case below.
      if (userTotal > paymentAmt) {
        const scale = paymentAmt / userTotal;
        const fixedKeys = new Set(fixedRows.map((inv) => inv.rowKey));
        return prev.map((inv) => {
          if (!selectedInvoices.includes(inv.rowKey)) return resetRow(inv);
          if (!fixedKeys.has(inv.rowKey)) {
            return inv.paymentAmt ? { ...inv, paymentAmt: "" } : inv;
          }
          const scaled = +(toNum(inv.paymentAmt) * scale).toFixed(2);
          return { ...inv, paymentAmt: String(scaled) };
        });
      }

      // Payment cap is the invoice total minus what's already paid minus the
      // row's TDS amount (e.g. entered in its own TDS field, or from global
      // TDS %), converted to org currency so it can be compared against the
      // org-currency payment pool. A row that's already fully settled has a
      // cap of 0, so Math.min(remaining, cap) naturally gives it nothing —
      // no special casing needed for the waterfall. Only the even-split
      // divisor needs to exclude zero-cap rows so they don't dilute the split.
      const capByRowKey = new Map(
        selected.map((inv) => [inv.rowKey, getInvoiceCap(inv).cap * rate]),
      );
      const autoRows = selected.filter((inv) => !inv.userEdited);
      const positiveCapRows = autoRows.filter((inv) => capByRowKey.get(inv.rowKey) > 0);
      let remaining = Math.max(0, paymentAmt - userTotal);

      const totalOutstanding = positiveCapRows.reduce(
        (s, inv) => s + capByRowKey.get(inv.rowKey),
        0,
      );
      const useEvenSplit = totalOutstanding <= 0;
      const perEven = useEvenSplit && positiveCapRows.length > 0
        ? +(remaining / positiveCapRows.length).toFixed(2)
        : 0;

      return prev.map((inv) => {
        if (!selectedInvoices.includes(inv.rowKey)) return resetRow(inv);
        if (inv.userEdited) return inv;
        // paymentAmt > 0 guaranteed here — the paymentAmt <= 0 case returns
        // early above (clearing every non-userEdited selected row).

        const cap = capByRowKey.get(inv.rowKey) ?? 0;
        let give;
        if (useEvenSplit) {
          give = cap > 0 ? perEven : 0;
        } else {
          give = Math.min(remaining, cap);
          remaining -= give;
        }

        // give is in org currency — convert back to this invoice's own
        // currency before storing, since paymentAmt/cap/display are all in
        // invoice currency everywhere else in the component. The org-currency
        // amount is stored as-is (paymentAmtOrg) so the converted-value
        // display can show it directly instead of re-deriving it by
        // multiplying the already-rounded invoice-currency value back out
        // (which compounds rounding error, e.g. 1000 -> 11.98 -> 1000.33).
        const giveInInvoiceCurrency = give / rate;
        const next = give > 0 ? String(+giveInInvoiceCurrency.toFixed(2)) : "";
        const nextOrg = give > 0 ? String(+give.toFixed(2)) : "";
        return inv.paymentAmt === next && inv.paymentAmtOrg === nextOrg && inv.tdsLocked === true
          ? inv
          : { ...inv, paymentAmt: next, paymentAmtOrg: nextOrg, tdsLocked: true };
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectivePaymentAmount, selectedInvoices.join(","), selectedTdsKey, currencyRate, orgCurrencySymbol]);

  const submitPayment = async () => {
    // Clear previous validations
    setValidations({});

    // Validation
    if (!customerOption?.value) {
      setValidations(prev => ({ ...prev, customer_id: "Please select a customer" }));
      setSnackbarMessage("Please select a customer");
      setSnackbarStatus(false);
      handleSnackbarShow();
      return;
    }

    // Valid as long as something is available to allocate — either typed
    // directly, or via the customer's included unallocated balance.
    if (effectivePaymentAmount <= 0) {
      setValidations(prev => ({ ...prev, payment_amount: "Please enter a valid payment amount" }));
      notify("Please enter a valid payment amount", false);
      return;
    }

    // Total invoice payable amounts (each in its own invoice currency) must
    // not exceed the global Payment Amount (organization currency) — use the
    // waterfall's own org-currency allocation (paymentAmtOrg) when available,
    // since re-deriving it by multiplying the already-rounded invoice-currency
    // paymentAmt back out compounds rounding error (e.g. 1000 -> 588.24 ->
    // 1000.40 instead of exactly 1000).
    const selected = invoices.filter((inv) => selectedInvoices.includes(inv.rowKey));

    // Validate payment input against pending_amount_fc for each selected invoice
    for (const inv of selected) {
      const enteredAmt = toNum(inv.paymentAmt);
      const pendingFc = toNum(inv.pendingAmountFc ?? inv.outstanding);
      const { cap } = getInvoiceCap(inv);
      const maxAllowed = cap > 0 ? cap : pendingFc;

      if (enteredAmt > maxAllowed + 0.01) {
        const message = `Payment amount for invoice ${inv.invoiceNo} (${formatWithSymbol(enteredAmt, inv.currencySymbol)}) cannot exceed pending amount (${formatWithSymbol(maxAllowed, inv.currencySymbol)})`;
        setValidations((prev) => ({ ...prev, invoices: message }));
        notify(message, false);
        return;
      }
    }

    const totalPayable = selected.reduce(
      (sum, inv) =>
        sum + (inv.paymentAmtOrg ? toNum(inv.paymentAmtOrg) : toNum(inv.paymentAmt) * (getEffectiveRate(inv) || 1)),
      0,
    );
    const globalAmt = effectivePaymentAmount;

    // Compare with a small epsilon — totalPayable is a sum of floating-point
    // waterfall allocations, so it can exceed globalAmt by a fraction of a
    // paisa even when both round to the exact same displayed value.
    if (toNum(totalPayable) - globalAmt > 0.01) {
      const message = `Invoice payable total (${formatCurrency(totalPayable)}) exceeds Payment Amount (${formatCurrency(globalAmt)})`;
      setValidations(prev => ({ ...prev, payment_amount: message }));
      notify(message, false);
      return;
    }

    // Extract payment type value (handle both {value, label} object and string)
    const paymentTypeValue = typeof paymentType === "object"
      ? (paymentType?.value ?? paymentType?.id ?? "")
      : (paymentType ?? "");

    // Validation for payment type
    if (!paymentTypeValue) {
      setValidations((prev) => ({ ...prev, payment_type_id: "Please select a payment type" }));
      notify("Please select a payment type", false);
      return;
    }

    setIsSaving(true);

    try {
      // Convert uploaded files to base64 (Flutter format)
      const base64Attachments = uploadedFiles.map((file) => ({
        file_name: sanitizeInput(file.name),
        base64: file.base64,
      }));

      // Build invoice list from selected invoices (map IDs to actual invoice objects)
      const invoiceList = selected.map((inv) => {
        const rate = getEffectiveRate(inv) || 1;
        const enteredAmount = toNum(inv.paymentAmt || 0);
        // Auto-calculate converted_amount = entered_amount * exchange_rate
        const convertedAmount = +(enteredAmount * rate).toFixed(2);

        return {
          id: sanitizeInput(inv.id),
          amount: String(sanitizeNumericInput(inv.paymentAmt || 0)),
          tds_amount: String(sanitizeNumericInput(inv.tdsAmt || 0)),
          exchange_rate: String(sanitizeNumericInput(rate || 1)),
          converted_amount: String(sanitizeNumericInput(convertedAmount || 0)),
        };
      });

      // Extract payment type label for comparison (handle both formats)
      const paymentTypeLabel = typeof paymentType === "object"
        ? (paymentType?.label ?? "")
        : (paymentType ?? "");

      const customerIdValue = typeof customerOption === "object"
        ? (customerOption?.value ?? customerOption?.id ?? "")
        : (customerOption ?? "");

      const payload = {
        amount: String(sanitizeNumericInput(globalPaymentAmount)),
        // Portion of the customer's existing unallocated balance applied to
        // this payment (when "Include Unallocated Amt" is on) — sent
        // separately from `amount` so the backend can deduct it from the
        // stored balance instead of double-counting it as new leftover.
        used_unallocated_balance: includeUnallocated
          ? String(sanitizeNumericInput(customerData.unallocatedBalance))
          : "0",
        tds_percentage: String(sanitizeNumericInput(globalTdsPercent || 0)),
        currency_rate: String(sanitizeNumericInput(currencyRate || 1)),
        customer_id: sanitizeInput(customerIdValue),
        payment_date: sanitizeInput(formatLocalDate(paymentDate)),
        payment_type_id: sanitizeInput(paymentTypeValue),
        balance_checked: false,
        invoices: invoiceList,
        cheque_number: paymentTypeLabel?.toLowerCase() === "cheque" ? sanitizeInput(chequeNumber) : "",
        cheque_date: paymentTypeLabel?.toLowerCase() === "cheque" && chequeDate ? sanitizeInput(chequeDate) : "",
        account_number: paymentTypeLabel?.toLowerCase() === "neft" ? sanitizeInput(accountNo) : "",
        account_name: paymentTypeLabel?.toLowerCase() === "neft" ? sanitizeInput(accountName) : "",
        net_banking_id: paymentTypeLabel?.toLowerCase() === "neft" ? sanitizeInput(netBankingId) : "",
        comment: sanitizeInput(remarks),
        attachments: base64Attachments,
      };

      const response = await paymentApi.createPaymentApi(payload);

      const body = response?.data;
      const ok = body?.success === true;
      const errorPayload = body?.error || body?.errors || null;

      if (ok) {
        notify(body?.message || "Payment created successfully", true);

        setTimeout(() => {
          navigate("/payment/list");
        }, 1500);
      } else if (errorPayload) {
        console.error("Server validation errors:", errorPayload);

        // Handle Laravel validation errors structure where each field holds an array of messages
        const formattedErrors = {};
        Object.keys(errorPayload).forEach((key) => {
          const errVal = errorPayload[key];
          const errorMessage = Array.isArray(errVal) ? errVal[0] : (typeof errVal === "string" ? errVal : "");

          formattedErrors[key] = errorMessage;
          if (key === "amount") {
            formattedErrors["payment_amount"] = errorMessage;
          }
        });
        setValidations(formattedErrors);

        notify(body?.message || "Please check the form and try again", false);
      } else {
        notify(body?.message || "Unable to save payment", false);
      }
    } catch (error) {
      console.error("Error submitting payment:", error);
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to create payment";
      notify(errorMsg, false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  // Format a Date as YYYY-MM-DD using its LOCAL date parts (not UTC), so a
  // date picked in a timezone ahead of UTC doesn't roll back a day when
  // toISOString() converts it to UTC.
  const formatLocalDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Format an amount with a specific currency symbol (falls back to ₹).
  const formatWithSymbol = (amount, symbol) => {
    const num = Number.parseFloat(amount);
    const display = Number.isNaN(num)
      ? "0.00"
      : num.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
    return `${decodeHtmlEntity(symbol) || "₹"} ${display}`;
  };

  // Renders an amount plus its converted value (amount × global Currency
  // Rate) underneath, for the invoice table's Without Tax/Total/Received/
  // Outstanding/TDS/Payment Amount columns. The converted value is shown in
  // the organization's own currency (from the backend), not the invoice's.
  // Only shown for the currently-selected invoice row (showConverted).
  // Renders primary FC amount with secondary BC (INR) conversion underneath.
  // Primary display: currency_symbol + total_amount_fc.
  // Secondary helper text: org_currency_symbol + total_amount_bc (only when foreign currency AND invoice is selected).
  const AmountWithConverted = ({ amount, symbol, invoice, amountBc, showConverted }) => {
    const rate = invoice ? getEffectiveRate(invoice) : toNum(currencyRate);
    const decodedOrgSymbol = decodeHtmlEntity(orgCurrencySymbol);
    const decodedInvSymbol = decodeHtmlEntity(symbol);
    const isForeignCurrency =
      (decodedOrgSymbol && decodedInvSymbol && decodedOrgSymbol !== decodedInvSymbol) ||
      rate > 1;

    // Prefer calculation via effective exchange rate; fallback to amountBc if rate is not set
    const convertedBc =
      rate > 0
        ? +(toNum(amount) * rate).toFixed(2)
        : (amountBc !== undefined && amountBc !== null && toNum(amountBc) > 0 ? toNum(amountBc) : 0);

    return (
      <div>
        <div>{formatWithSymbol(amount, symbol)}</div>
        {isForeignCurrency && showConverted && convertedBc > 0 && (
          <div className="small text-muted">
            {formatWithSymbol(convertedBc, orgCurrencySymbol)}
          </div>
        )}
      </div>
    );
  };

  // Fetch payment types on component mount
  useEffect(() => {
    const fetchPaymentTypes = async () => {
      setLoadingPaymentTypes(true);
      try {
        const response = await paymentApi.getPaymentTypesApi();
        const body = response?.data;

        if (body?.success || Array.isArray(body)) {
          // Handle different response shapes
          let types = [];

          if (Array.isArray(body)) {
            // If response is directly an array
            types = body;
          } else if (Array.isArray(body?.suggestionlist)) {
            types = body.suggestionlist;
          }

          // Normalize to {value, label} format using the API's normalizer
          const normalizedTypes = paymentApi.normalizePaymentTypes(types);

          if (normalizedTypes.length > 0) {
            setPaymentTypes(normalizedTypes);
            // Set first payment type as default
            setPaymentType(normalizedTypes[0]);
          }
        }
      } catch (error) {
        console.error("Error fetching payment types:", error);
      } finally {
        setLoadingPaymentTypes(false);
      }
    };

    fetchPaymentTypes();
  }, []);

  useEffect(() => {
    if (id != null) {
      setPaymentId(id);
    }
  }, [id]);

  return (
    <div className="page-container payment-page">
      <ToastContainer position="top-end" className="p-3">
        <Toast
          show={showSnackbar}
          onClose={handleSnackbarClose}
          delay={3000}
          autohide
          bg={snackbarStatus ? "success" : "danger"}
        >
          <Toast.Body className="text-white">{snackbarMessage}</Toast.Body>
        </Toast>
      </ToastContainer>

      <div className="payment-header d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center gap-3">
          <img
            src={Back}
            alt="back"
            className="back-icon"
            onClick={() => navigate(-1)}
          />
          <div className="page-title">New Payment</div>
        </div>

        <div>
          <Button
            variant="light"
            className="btn-cancel me-2"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            className="btn-save"
            onClick={submitPayment}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <Spinner animation="border" size="sm" /> Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </div>

      <div className="row col-12 ">
        <div className="col-md-5 col-sm-12 mt-3">
          <div className="payment-card">
            <h5 className="card-title">Customer details</h5>
            <div className="customer-card">
              <label className="field-label">
                Customer<span className="required">*</span>
              </label>
              <SearchWithAPI
                onDropdownChange={handleCustomerDropdownChange}
                apiUrl={apiUrl + "get_customers"}
                params={{}}
                placeholder={"Select Customer"}
                selectedOption={customerOption}
              />
              <ErrorMessage message={validations.customer_id} />
            </div>
            {/* Customer Information */}
            {customerOption && (
              <div>
                <div className="customer-info-card">
                  {/* Company Info */}
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <div
                      className="rounded-circle bg-white d-flex align-items-center justify-content-center"
                      style={{
                        width: "64px",
                        height: "64px",
                        border: "1px solid #E5E7EB",
                      }}
                    >
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold fs-5"
                        style={{
                          width: "56px",
                          height: "56px",
                          backgroundColor: "#1E40AF",
                        }}
                      >
                        {(customerData.customerName || "?")
                          .slice(0, 2)
                          .toUpperCase()}
                      </div>
                    </div>
                    <div>
                      <div className="fw-semibold fs-6 text-dark mb-1">
                        {customerData.customerName}
                      </div>
                      <div className="small text-muted">
                        Revenue : {formatCurrency(customerData.revenue)}
                      </div>
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <Row className="g-2">
                    <Col xs={6}>
                      <div className="stat-box stat-box-1">
                        <div className="stat-label">Total Invoices</div>
                        <div className="stat-value">
                          {customerData.totalInvoices}
                        </div>
                      </div>
                    </Col>

                    <Col xs={6}>
                      <div className="stat-box stat-box-2">
                        <div className="stat-label">Total payment</div>
                        <div className="stat-value">
                          ₹ {customerData.totalPayment.toLocaleString("en-IN")}
                        </div>
                      </div>
                    </Col>

                    <Col xs={6}>
                      <div className="stat-box stat-box-3">
                        <div className="stat-label">Amount Received</div>
                        <div className="stat-value">
                          ₹{customerData.amountReceived.toLocaleString("en-IN")}
                        </div>
                      </div>
                    </Col>

                    <Col xs={6}>
                      <div className="stat-box stat-box-4">
                        <div className="stat-label">Outstanding</div>
                        <div className="stat-value">
                          ₹{customerData.outstanding.toLocaleString("en-IN")}
                        </div>
                      </div>
                    </Col>

                    {customerData.unallocatedBalance > 0 && (
                      <Col xs={6}>
                        <div className="stat-box stat-box-1" title="Unallocated amount from prior payments, available to apply toward invoices">
                          <div className="stat-label">Unallocated Balance</div>
                          <div className="stat-value">
                            {orgCurrencySymbol || "₹"}{customerData.unallocatedBalance.toLocaleString("en-IN")}
                          </div>
                        </div>
                      </Col>
                    )}
                  </Row>
                </div>
              </div>
            )}
          </div>
        </div>
        {/* Left Column */}
        <div className="col-md-7 col-sm-12 mt-3">
          {/* Payment Details Section */}
          <div className="payment-card">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="card-title">Payment details</div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  color: "#535E83",
                  fontSize: "13px",
                  fontWeight: "500",
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                  padding: "6px 10px",
                  background: "#fff",
                }}
              >
                <img
                  src={Calendar}
                  alt=""
                  style={{ width: "15px", height: "15px", flexShrink: 0, cursor: "pointer" }}
                  onClick={() => paymentDateInputRef.current?.showPicker?.()}
                />
                <input
                  ref={paymentDateInputRef}
                  type="date"
                  className="payment-date-input-no-icon"
                  value={formatLocalDate(paymentDate)}
                  max={formatLocalDate(new Date())}
                  onChange={(e) => {
                    if (!e.target.value) return;
                    // The max attribute only constrains the native picker UI —
                    // typed/manual entry still fires onChange with an
                    // out-of-range value in most browsers, so re-apply the
                    // same "not in the future" rule here.
                    const entered = new Date(`${e.target.value}T00:00:00`);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    setPaymentDate(entered > today ? today : entered);
                  }}
                  style={{
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    color: "#535E83",
                    fontSize: "13px",
                    fontWeight: "500",
                    fontFamily: "inherit",
                    padding: 0,
                  }}
                />
              </div>
            </div>
            <style>
              {`
                /* Keep the native calendar icon clickable (still opens the
                   picker), but no longer stretched over the whole input —
                   that was swallowing every click/keystroke and made the
                   field click-only instead of also allowing manual typing. */
                .payment-date-input-no-icon::-webkit-calendar-picker-indicator {
                  opacity: 0;
                  cursor: pointer;
                }
              `}
            </style>
            <div className="d-flex flex-wrap align-items-start">

              <div className="col-12 col-lg-5 mt-3">
                <div className="d-flex align-items-center justify-content-between mb-1" style={{ minHeight: "24px" }}>
                  <label className="field-label mb-0">Payment Amount</label>
                  {customerData.unallocatedBalance > 0 && (
                    <div
                      className="d-flex align-items-center gap-2"
                      style={{ cursor: "pointer", userSelect: "none" }}
                      title={`Add the customer's unallocated balance (${orgCurrencySymbol || "₹"}${customerData.unallocatedBalance.toLocaleString("en-IN")}) to the Payment Amount`}
                      onClick={() => setIncludeUnallocated((prev) => !prev)}
                    >
                      <span className="fs-13" style={{ color: "#535E83" }}>
                        Unallocated Amt
                      </span>
                      <div
                        role="switch"
                        aria-checked={includeUnallocated}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === " " || e.key === "Enter") {
                            e.preventDefault();
                            setIncludeUnallocated((prev) => !prev);
                          }
                        }}
                        style={{
                          width: "36px",
                          height: "20px",
                          backgroundColor: includeUnallocated ? "#3D4AF5" : "#D1D5DB",
                          borderRadius: "20px",
                          position: "relative",
                          transition: "background-color 0.2s ease",
                          flexShrink: 0,
                          cursor: "pointer",
                        }}
                      >
                        <div
                          style={{
                            width: "16px",
                            height: "16px",
                            backgroundColor: "#ffffff",
                            borderRadius: "50%",
                            position: "absolute",
                            top: "2px",
                            left: includeUnallocated ? "18px" : "2px",
                            transition: "left 0.2s ease",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <div className="position-relative">
                  <span
                    className="position-absolute top-50 translate-middle-y fw-semibold"
                    style={{ left: "12px" }}
                  >
                    {orgCurrencySymbol || "₹"}
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    value={globalPaymentAmount}
                    onChange={(e) => {
                      // Numeric only (integers and decimals) — strips letters/symbols.
                      const raw = sanitizeNumericInput(e.target.value);
                      setGlobalPaymentAmount(raw);
                      setValidations((prev) => ({ ...prev, payment_amount: null }));
                    }}
                    placeholder="Enter Amount"
                    style={{ paddingLeft: "30px" }}
                  />
                </div>
                {includeUnallocated && customerData.unallocatedBalance > 0 && (
                  <div className="small text-muted mt-1">
                    Available to allocate: {orgCurrencySymbol || "₹"}
                    {effectivePaymentAmount.toLocaleString("en-IN")}
                    {" "}({orgCurrencySymbol || "₹"}{toNum(globalPaymentAmount).toLocaleString("en-IN")} entered + {orgCurrencySymbol || "₹"}{customerData.unallocatedBalance.toLocaleString("en-IN")} unallocated)
                  </div>
                )}
                <ErrorMessage message={validations.payment_amount} />
              </div>

              <div className="col-12 col-lg-3 mt-3 ms-3">
                <div className="d-flex align-items-center mb-1" style={{ minHeight: "24px" }}>
                  <label className="field-label mb-0">TDS %</label>
                </div>
                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control"
                    value={globalTdsPercent}
                    onChange={(e) => handleGlobalTdsPercentChange(e.target.value)}
                    placeholder="Enter TDS %"
                    style={{ paddingRight: "24px" }}
                  />
                  <span
                    className="position-absolute top-50 translate-middle-y fw-semibold"
                    style={{ right: "12px" }}
                  >
                    %
                  </span>
                </div>
              </div>


              <div className="col-12 col-lg-7 mt-3">
                <label className="field-label sp-2">
                  Payment Type
                  {loadingPaymentTypes && (
                    <span style={{ fontSize: "11px", color: "#8790A1", marginLeft: "8px" }}>
                      (Loading...)
                    </span>
                  )}
                </label>
                <div className="d-flex gap-1 mt-2 px-2" style={{ flexWrap: "wrap" }}>
                  {paymentTypes.map((type) => {
                    // Handle both string and object formats
                    const typeKey = typeof type === "object" ? type.value : type;
                    const typeLabel = typeof type === "object" ? type.label : type;
                    const isChecked = typeof paymentType === "object"
                      ? paymentType?.value === typeKey
                      : paymentType === typeLabel;

                    return (
                      <div key={typeKey}>
                        <label
                          className={`form-check ${isChecked ? "form-check-checked" : ""}`}
                          htmlFor={`payment-${typeKey}`}
                        >
                          <input
                            className="form-check-input"
                            type="radio"
                            name="paymentType"
                            id={`payment-${typeKey}`}
                            value={typeLabel}
                            checked={isChecked}
                            onChange={() => {
                              setPaymentType(type);
                              setValidations((prev) => ({ ...prev, payment_type_id: null }));
                            }}
                            disabled={loadingPaymentTypes}
                          />{" "}
                          {typeLabel}
                        </label>
                      </div>
                    );
                  })}
                </div>
                <ErrorMessage message={validations.payment_type_id} />
                {paymentTypes.length === 0 && !loadingPaymentTypes && (
                  <div style={{ color: "#ff6b6b", fontSize: "12px", marginTop: "4px" }}>
                    No payment types available. Please try again later.
                  </div>
                )}
              </div>

              {/* Cheque Number and Date - Show only when Cheque is selected */}
              {(paymentType === "Cheque" || (typeof paymentType === "object" && paymentType?.label === "Cheque")) && (
                <>
                  <div className="col-12 col-md-6 mt-3">
                    <div className="mx-1">
                      <label className="field-label">Cheque Number</label>
                      <input
                        type="text"
                        className="form-control"
                        value={chequeNumber}
                        onChange={(e) => {
                          setChequeNumber(sanitize(e.target.value));
                          setValidations((prev) => ({ ...prev, cheque_number: null }));
                        }}
                        placeholder="Enter cheque number..."
                      />
                      <ErrorMessage message={validations.cheque_number} />
                    </div>
                  </div>

                  <div className="col-12 col-md-6 mt-3">
                    <div className="mx-1">
                      <label className="field-label">Date</label>
                      <div className="position-relative">
                        <input
                          type="date"
                          className="form-control"
                          value={chequeDate}
                          onChange={(e) => {
                            setChequeDate(e.target.value);
                            setValidations((prev) => ({ ...prev, cheque_date: null }));
                          }}
                          placeholder="dd/mm/yyyy"
                          style={{ paddingRight: "40px" }}
                        />
                        <FaCalendarAlt
                          className="position-absolute top-50 translate-middle-y text-muted"
                          style={{ right: "12px", pointerEvents: "none" }}
                        />
                      </div>
                      <ErrorMessage message={validations.cheque_date} />
                    </div>
                  </div>
                </>
              )}

              {(paymentType === "NEFT" || (typeof paymentType === "object" && paymentType?.label === "NEFT")) && (
                <>
                  <div className="col-12 col-md-4 mt-3">
                    <div className="mx-1">
                      <label className="field-label">Account No.</label>
                      <input
                        type="text"
                        className="form-control"
                        value={accountNo}
                        onChange={(e) => {
                          setAccountNo(sanitize(e.target.value));
                          setValidations((prev) => ({ ...prev, account_number: null }));
                        }}
                        placeholder="Enter here..."
                      />
                      <ErrorMessage message={validations.account_number} />
                    </div>
                  </div>

                  <div className="col-12 col-md-4 mt-3">
                    <div className="mx-1">
                      <label className="field-label">Account Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={accountName}
                        onChange={(e) => {
                          setAccountName(sanitize(e.target.value));
                          setValidations((prev) => ({ ...prev, account_name: null }));
                        }}
                        placeholder="Enter here..."
                      />
                      <ErrorMessage message={validations.account_name} />
                    </div>
                  </div>

                  <div className="col-12 col-md-4 mt-3">
                    <div className="mx-1">
                      <label className="field-label">Net Banking ID</label>
                      <input
                        type="text"
                        className="form-control"
                        value={netBankingId}
                        onChange={(e) => {
                          setNetBankingId(sanitize(e.target.value));
                          setValidations((prev) => ({ ...prev, net_banking_id: null }));
                        }}
                        placeholder="Enter here..."
                      />
                      <ErrorMessage message={validations.net_banking_id} />
                    </div>
                  </div>
                </>
              )}

              <div className="col-12 mt-3">
                <label className="field-label">Remarks</label>
                <textarea
                  className="form-control"
                  value={remarks}
                  onChange={(e) => {
                    setRemarks(sanitize(e.target.value));
                    setValidations((prev) => ({ ...prev, comment: null }));
                  }}
                  placeholder="Enter your comments here..."
                  rows={1}
                />
                <ErrorMessage message={validations.comment} />
              </div>

              <div className="col-12 mt-3">
                <label className="field-label">
                  Upload Payment Proof
                  {uploadedFiles.length > 0 && (
                    <span className="text-muted ms-2">
                      ({uploadedFiles.length} file
                      {uploadedFiles.length > 1 ? "s" : ""})
                    </span>
                  )}
                </label>

                {/* Uploaded Files Display */}
                {uploadedFiles.length > 0 && (
                  <div className="d-flex align-items-center gap-2 mb-3" style={{ overflowX: "auto", paddingBottom: "8px", width: "100%" }}>
                    {uploadedFiles.map((fileObj, index) => (
                      <div key={index} style={{ flex: "0 0 280px", width: "280px" }}>
                        <div className="d-flex align-items-center justify-content-between p-2 border rounded bg-light" style={{ height: "64px" }}>
                          <div className="d-flex align-items-center gap-2" style={{ minWidth: 0, flex: 1 }}>
                            <div
                              className="rounded d-flex align-items-center justify-content-center text-white flex-shrink-0"
                              style={{
                                width: "32px",
                                height: "32px",
                                backgroundColor: "#10B981",
                                fontSize: "11px",
                                fontWeight: "600",
                              }}
                            >
                              {fileObj.name.split(".").pop().toUpperCase()}
                            </div>
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div
                                className="fw-medium"
                                style={{
                                  fontSize: "13px",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                                title={fileObj.name}
                              >
                                {fileObj.name}
                              </div>
                              <div className="small text-muted">
                                {(fileObj.size / 1024).toFixed(2)} KB
                              </div>
                            </div>
                          </div>
                          <Button
                            variant="link"
                            size="sm"
                            onClick={() => removeFile(index)}
                            className="text-danger text-decoration-none flex-shrink-0"
                            style={{ fontSize: "18px", padding: "0 8px" }}
                          >
                            ✕
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload Area */}
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="d-flex align-items-center justify-content-center py-3 gap-3 border  rounded text-center file-upload-card"
                  style={{ cursor: "pointer", backgroundColor: "#ffffff" }}
                >
                  <div className="mb-2">
                    <img src={DragDrop} alt="Drag and Drop" />
                  </div>
                  <div className="d-flex flex-column align-items-start">
                    <div className="mb-1 drag-drop-text">
                      Drag and drop or{" "}
                      <span className="text-primary fw-medium">
                        Select a files
                      </span>
                    </div>
                    <div className="drag-drop-text2">
                      You can add multiple files (max 5MB each)
                    </div>
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileUpload}
                  className="d-none"
                  accept=".png,.jpg,.jpeg,.pdf,.doc,.docx"
                  multiple
                />
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}

        {/* Invoices Table - Full Width */}
        {customerOption && (
          <div className="col-12">
            <div className="payment-card mt-3">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="card-title">
                  INVOICES ({invoices.length})
                  <ErrorMessage message={validations.invoices} />
                </div>

                <div className="d-flex align-items-end gap-2">
                  {distinctInvoiceCurrencies.length > 1 && (
                    <div style={{ width: "160px" }}>
                      <label className="field-label mb-0">Currency</label>
                      <select
                        className="form-control form-select"
                        value={currencyFilter}
                        onChange={(e) => setCurrencyFilter(e.target.value)}
                      >
                        <option value="">All</option>
                        {distinctInvoiceCurrencies.map(([id, label]) => (
                          <option key={id} value={id}>
                            {label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {showCurrencyRate && (
                    <div style={{ width: "180px" }}>
                      <label className="field-label mb-0">Currency Rate</label>
                      <input
                        type="text"
                        className="form-control"
                        value={currencyRate}
                        onChange={(e) => setCurrencyRate(sanitizeNumericInput(e.target.value))}
                        placeholder="Enter Currency Rate"
                      />
                    </div>
                  )}

                  {/* Search */}
                  <div className="">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by invoice no..."
                      value={invoiceSearchTerm}
                      onChange={(e) => setInvoiceSearchTerm(sanitizeInput(e.target.value))}
                      style={{ maxWidth: "300px" }}
                    />
                  </div>

                  {/* Show/hide invoice table columns */}
                  <button
                    type="button"
                    className="btn btn-light border"
                    onClick={() => setShowColumnModal(true)}
                    title="Customize Columns"
                    style={{
                      width: "36px",
                      height: "36px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: 0,
                    }}
                  >
                    <img src={Customize} alt="Customize" style={{ width: "16px", height: "16px" }} />
                  </button>
                </div>
              </div>
              {/* Invoice Table - Matches PaymentForm.jsx structure */}
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead className="bg-light">
                    <tr className="bg-light">
                      <th className="px-2 py-3 small fw-semibold text-muted border-0" style={{ width: "50px" }}>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={
                            selectedInvoices.length === selectableCount &&
                            selectableCount > 0
                          }
                          onChange={handleSelectAll}
                          ref={(input) => {
                            if (input) {
                              input.indeterminate =
                                selectedInvoices.length > 0 &&
                                selectedInvoices.length < selectableCount;
                            }
                          }}
                        />
                      </th>
                      {visibleColumns["Invoice No."] && (
                        <th className="px-2 py-3 small fw-semibold text-muted border-0">
                          Invoice No.
                        </th>
                      )}
                      {visibleColumns["Invoice Date"] && (
                        <th className="px-2 py-3 small fw-semibold text-muted border-0">
                          Invoice Date
                        </th>
                      )}
                      {visibleColumns["Without Tax"] && (
                        <th className="px-2 py-3 small fw-semibold text-muted border-0">
                          Without Tax
                        </th>
                      )}
                      {visibleColumns["Total"] && (
                        <th className="px-2 py-3 small fw-semibold text-muted border-0">
                          Total
                        </th>
                      )}
                      {visibleColumns["Received"] && (
                        <th className="px-2 py-3 small fw-semibold text-muted border-0">
                          Received
                        </th>
                      )}
                      {visibleColumns["Outstanding"] && (
                        <th className="px-2 py-3 small fw-semibold text-muted border-0">
                          Outstanding
                        </th>
                      )}
                      {visibleColumns["Currency Value"] && (
                        <th className="px-2 py-3 small fw-semibold text-muted border-0">
                          Currency Value
                        </th>
                      )}
                      {visibleColumns["TDS"] && (
                        <th className="px-2 py-3 small fw-semibold text-muted border-0">
                          TDS
                        </th>
                      )}
                      {visibleColumns["TCS"] && (
                        <th className="px-2 py-3 small fw-semibold text-muted border-0">
                          TCS
                        </th>
                      )}
                      {visibleColumns["Payment Amount"] && (
                        <th className="px-2 py-3 small fw-semibold text-muted border-0">
                          Payment Amount
                        </th>
                      )}
                      {visibleColumns["Difference"] && (
                        <th className="px-2 py-3 small fw-semibold text-muted border-0">
                          Difference
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredInvoices.length === 0 ? (
                      <tr>
                        <td
                          colSpan={1 + Object.values(visibleColumns).filter(Boolean).length}
                          className="text-center p-4 text-muted"
                        >
                          {invoices.length === 0
                            ? "No outstanding invoices for this customer."
                            : "No invoices match your filter/search."}
                        </td>
                      </tr>
                    ) : (
                      filteredInvoices.map((invoice) => {
                        const isSelected = selectedInvoices.includes(invoice.rowKey);
                        const selectable = isInvoiceSelectable(invoice);
                        const hasGlobalAmount = effectivePaymentAmount > 0;
                        const { cap } = getInvoiceCap(invoice);
                        const paymentDisabled = !isSelected || !hasGlobalAmount;

                        // Full = payable amount fully covers this row's cap.
                        // Partial = something entered, but less than the cap.
                        const enteredAmt = toNum(invoice.paymentAmt);
                        const paidStatus =
                          enteredAmt <= 0 || cap <= 0
                            ? null
                            : enteredAmt >= cap
                            ? "full"
                            : "partial";
                        const paymentAmountFieldStyle = {
                          width: "120px",
                          backgroundColor:
                            paidStatus === "full"
                              ? "#e6f7ec"
                              : paidStatus === "partial"
                              ? "#fff8e1"
                              : paymentDisabled
                              ? "#f5f5f5"
                              : "white",
                          border:
                            paidStatus === "full"
                              ? "1px solid #2e7d32"
                              : paidStatus === "partial"
                              ? "1px solid #f9a825"
                              : undefined,
                          boxShadow:
                            paidStatus === "full"
                              ? "0 0 0 1px rgba(46, 125, 50, 0.25)"
                              : paidStatus === "partial"
                              ? "0 0 0 1px rgba(249, 168, 37, 0.25)"
                              : undefined,
                        };

                        return (
                        <tr
                          key={invoice.rowKey}
                          className="border-bottom"
                          style={{ cursor: "pointer" }}
                        >
                          <td className="p-2">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={isSelected}
                              disabled={!selectable}
                              onChange={() => handleInvoiceSelect(invoice.rowKey)}
                              title={selectable ? "" : "Different currency from selected invoices"}
                              style={selectable ? undefined : { cursor: "not-allowed", opacity: 0.5 }}
                            />
                          </td>
                          {visibleColumns["Invoice No."] && (
                            <td className="p-2">
                              <div className="fw-semibold">{invoice.invoiceNo || ``}</div>
                            </td>
                          )}
                          {visibleColumns["Invoice Date"] && (
                            <td className="p-2">
                              <div className="fw-semibold">{invoice.invoiceDate || ``}</div>
                            </td>
                          )}
                          {visibleColumns["Without Tax"] && (
                            <td className="p-2 ">
                              <AmountWithConverted amount={invoice.withoutTax} symbol={invoice.currencySymbol} amountBc={invoice.withoutTaxBc} invoice={invoice} showConverted={isSelected} />
                            </td>
                          )}
                          {visibleColumns["Total"] && (
                            <td className="p-2 ">
                              <AmountWithConverted amount={invoice.totalAmount} symbol={invoice.currencySymbol} amountBc={invoice.totalAmountBc} invoice={invoice} showConverted={isSelected} />
                            </td>
                          )}
                          {visibleColumns["Received"] && (
                            <td className="p-2 ">
                              <div className="d-flex align-items-center gap-2">
                                <AmountWithConverted amount={invoice.paidAmount} symbol={invoice.currencySymbol} amountBc={invoice.paidAmountBc} invoice={invoice} showConverted={isSelected} />
                                {toNum(invoice.paidAmount) > 0 && (
                                  <span
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openPaidHistory(invoice);
                                    }}
                                    title="View payment history"
                                    style={{
                                      cursor: "pointer",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      width: "22px",
                                      height: "22px",
                                      borderRadius: "100px",
                                      border: "1px solid #E5E5E5",
                                      flexShrink: 0,
                                    }}
                                  >
                                    <HistoryIc style={{ width: "12px", height: "12px" }} />
                                  </span>
                                )}
                              </div>
                            </td>
                          )}
                          {visibleColumns["Outstanding"] && (
                            <td className="p-2 text-danger ">
                              <AmountWithConverted amount={invoice.outstanding} symbol={invoice.currencySymbol} amountBc={invoice.pendingAmountBc} invoice={invoice} showConverted={isSelected} />
                            </td>
                          )}
                          {visibleColumns["Currency Value"] && (
                            <td className="p-2 ">
                              {invoice.currencyRate || '—'}
                            </td>
                          )}
                          {visibleColumns["TDS"] && (
                            <td className="p-2">
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={
                                  invoice.tdsAmt
                                    ? +(toNum(invoice.tdsAmt) * (getEffectiveRate(invoice) || 1)).toFixed(2)
                                    : ''
                                }
                                onChange={(e) =>
                                  handleTdsAmtChange(invoice.rowKey, e.target.value)
                                }
                                step="0.01"
                                min="0"
                                placeholder="0.00"
                                disabled={!isSelected}
                                title={`In ${orgCurrencySymbol || "organization currency"}`}
                                style={{
                                  width: "120px",
                                  backgroundColor: isSelected ? 'white' : '#f5f5f5',
                                }}
                              />
                              {isSelected && getEffectiveRate(invoice) > 0 && toNum(invoice.tdsAmt) > 0 && (
                                <div className="small text-muted mt-1">
                                  {formatWithSymbol(invoice.tdsAmt, invoice.currencySymbol)}
                                </div>
                              )}
                            </td>
                          )}
                          {visibleColumns["TCS"] && (
                            <td className="p-2 ">
                              <span className="small">
                                {invoice.tcs_amount_bc || invoice.tcsAmount || '0'} ({invoice.tcs_percentage || invoice.tcsPercent || '0'}%)
                              </span>
                            </td>
                          )}
                          {visibleColumns["Payment Amount"] && (
                            <td className="p-2">
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={invoice.paymentAmt || ''}
                                readOnly
                                step="0.01"
                                min="0"
                                max={cap}
                                placeholder="0.00"
                                disabled={paymentDisabled}
                                title={
                                  !hasGlobalAmount
                                    ? "Enter a global Payment Amount first"
                                    : paidStatus === "full"
                                    ? "Fully paid"
                                    : paidStatus === "partial"
                                    ? "Partially paid"
                                    : "Auto-calculated from Payment Amount"
                                }
                                style={paymentAmountFieldStyle}
                              />
                              {isSelected && getEffectiveRate(invoice) > 0 && toNum(invoice.paymentAmt) > 0 && (
                                <div className="small text-muted mt-1">
                                  {formatWithSymbol(
                                    invoice.paymentAmtOrg
                                      ? toNum(invoice.paymentAmtOrg)
                                      : toNum(invoice.paymentAmt) * getEffectiveRate(invoice),
                                    orgCurrencySymbol,
                                  )}
                                </div>
                              )}
                            </td>
                          )}
                          {visibleColumns["Difference"] && (
                            <td className="p-2">
                              {(() => {
                                // Remaining payable capacity on this invoice after
                                // the current Payment Amount is applied — 0 once
                                // the row is fully paid off.
                                const remaining = Math.max(cap - toNum(invoice.paymentAmt), 0);
                                const rate = getEffectiveRate(invoice);
                                return (
                                  <>
                                    <span
                                      className={remaining > 0 ? "text-danger" : "text-muted"}
                                      title="Remaining payable amount on this invoice"
                                    >
                                      {formatWithSymbol(remaining, invoice.currencySymbol)}
                                    </span>
                                    {isSelected && rate > 0 && remaining > 0 && (
                                      <div className="small text-muted mt-1">
                                        {formatWithSymbol(remaining * rate, orgCurrencySymbol)}
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </td>
                          )}
                        </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Allocation Summary - Matches PaymentForm.jsx */}
              {selectedInvoices.length > 0 && (() => {
                // allocatedTotal is converted to org currency (via the global
                // Currency Rate) so it's comparable against effectivePaymentAmount,
                // which is in the organization's currency. Uses the waterfall's
                // own org-currency allocation (paymentAmtOrg) when available to
                // avoid compounding rounding error from re-deriving it off the
                // already-rounded invoice-currency value.
                const { tdsTotal, allocatedTotal } = invoices
                  .filter((inv) => selectedInvoices.includes(inv.rowKey))
                  .reduce(
                    (acc, inv) => ({
                      tdsTotal: acc.tdsTotal + toNum(inv.tdsAmt),
                      allocatedTotal:
                        acc.allocatedTotal +
                        (inv.paymentAmtOrg ? toNum(inv.paymentAmtOrg) : toNum(inv.paymentAmt) * (getEffectiveRate(inv) || 1)),
                    }),
                    { tdsTotal: 0, allocatedTotal: 0 },
                  );
                const paymentAmt = effectivePaymentAmount;

                return (
                  <div className="mt-3 p-3 bg-light border rounded" style={{ fontSize: "13px" }}>
                    <div className="d-flex flex-wrap gap-3">
                      <div>
                        <span className="text-muted">Selected: </span>
                        <span className="fw-semibold">{selectedInvoices.length}</span>
                      </div>
                      <div>
                        <span className="text-muted">TDS Amount: </span>
                        <span className="fw-semibold">{formatCurrency(tdsTotal)}</span>
                      </div>
                      <div>
                        <span className="text-muted">Payment Amount: </span>
                        <span className="fw-semibold">{formatCurrency(paymentAmt)}</span>
                      </div>
                      <div>
                        <span className="text-muted">Allocated: </span>
                        <span className="fw-semibold">{formatCurrency(allocatedTotal)}</span>
                      </div>
                      {paymentAmt > 0 && (
                        <div className="text-warning fw-semibold">
                          {/* Epsilon-tolerant — allocatedTotal is a sum of floating-point
                              waterfall allocations, so it can differ from paymentAmt by a
                              fraction of a paisa even when both round to the same display value. */}
                          {allocatedTotal - paymentAmt > 0.01 ? (
                            <span className="text-danger">Over-allocated by {formatCurrency(allocatedTotal - paymentAmt)}</span>
                          ) : paymentAmt - allocatedTotal > 0.01 ? (
                            <span className="text-warning">Under-allocated by {formatCurrency(paymentAmt - allocatedTotal)}</span>
                          ) : null}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </div>

      {/* Payment Invoice Modal */}
      <PaymentInvoiceModal
        show={showInvoiceModal}
        onHide={handleCloseModal}
        invoice={selectedInvoice}
        formatCurrency={formatCurrency}
      />

      {/* Customize (show/hide) invoice table columns */}
      {showColumnModal && (
        <div
          onClick={() => setShowColumnModal(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: "#fff",
              padding: "24px",
              borderRadius: "12px",
              boxShadow: "0px 4px 12px rgba(0,0,0,0.2)",
              width: "300px",
              maxHeight: "80vh",
              overflowY: "auto",
            }}
          >
            <div style={{ fontWeight: "600", fontSize: "16px", marginBottom: "16px" }}>
              Select Columns
            </div>
            {INVOICE_COLUMN_OPTIONS.map((label) => (
              <label
                key={label}
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "10px",
                  fontSize: "14px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={!!visibleColumns[label]}
                  onChange={() => toggleColumn(label)}
                  style={{ marginRight: "8px" }}
                />
                {label}
              </label>
            ))}
            <button
              type="button"
              onClick={() => setShowColumnModal(false)}
              style={{
                marginTop: "16px",
                width: "100%",
                padding: "10px",
                borderRadius: "8px",
                border: "none",
                backgroundColor: "#3D4AF5",
                color: "#fff",
                fontWeight: "500",
                cursor: "pointer",
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Paid History Modal — same design as invoice-view.jsx's Paid History tab */}
      {showPaidHistory && (
        <div
          onClick={closePaidHistory}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 1000,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: "16px",
              width: "460px",
              maxHeight: "80vh",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 4px 24px rgba(0,0,0,0.2)",
            }}
          >
            <div className="d-flex justify-content-between align-items-center p-3" style={{ borderBottom: "1px solid #ebebeb" }}>
              <div>
                <div className="fs-16 fw-600">Paid History</div>
                {paidHistoryInvoice?.invoiceNo && (
                  <div className="fs-13 text-muted">{sanitizeInput(paidHistoryInvoice.invoiceNo)}</div>
                )}
              </div>
              <span onClick={closePaidHistory} style={{ cursor: "pointer", fontSize: "18px" }}>✖</span>
            </div>

            <div style={{ overflowY: "auto", padding: "16px" }}>
              {isPaidHistoryLoading ? (
                <div className="d-flex justify-content-center py-4">
                  <Loader height="150px" />
                </div>
              ) : paidHistory.length === 0 ? (
                <p className="text-center text-muted py-4">No payment history available</p>
              ) : (
                <div className="d-flex flex-column ms-3">
                  {paidHistory.map((txn, idx) => (
                    <div key={txn.id ?? idx} className="d-flex mb-4 position-relative">
                      {/* Green tick */}
                      <div
                        className="d-flex align-items-center justify-content-center rounded-circle"
                        style={{
                          backgroundColor: "#E6FFF1",
                          width: "40px",
                          height: "40px",
                          marginRight: "15px",
                          marginLeft: "-19px",
                          zIndex: 1,
                        }}
                      >
                        <div
                          className="d-flex align-items-center justify-content-center rounded-circle"
                          style={{
                            backgroundColor: "#4EBD7E",
                            width: "20px",
                            height: "20px",
                            color: "white",
                            fontSize: "14px",
                            fontWeight: "bold",
                          }}
                        >
                          ✓
                        </div>
                      </div>

                      {/* Vertical dashed line */}
                      {idx !== paidHistory.length - 1 && (
                        <div
                          className="position-absolute start-0"
                          style={{
                            borderLeft: "2px dashed #3469E7",
                            height: "calc(100% + 16px)",
                            left: "8px",
                            top: "10px",
                          }}
                        />
                      )}

                      {/* Payment details */}
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center mb-2 flex-wrap">
                          <span className="fw-700 fs-16 me-2" style={{ color: "#2C8C53" }}>
                            +{formatCurrency(toNum(txn.amount))}
                          </span>
                          <div style={{ width: "4px", height: "4px", borderRadius: "50%", backgroundColor: "#D9D9D9" }} className="me-2"></div>
                          <span className="badge rounded-pill fs-11 fw-600" style={{ backgroundColor: "#4F46E5" }}>
                            {sanitizeInput(txn.payment_method ?? "-")}
                          </span>
                        </div>
                        {(txn.payment_code || txn.invoice_code) && (
                          <p className="m-0 fs-13 fw-500 mb-1" style={{ color: "#1F2937" }}>
                            {sanitizeInput(txn.payment_code ?? "-")}
                            {txn.invoice_code ? ` · ${sanitizeInput(txn.invoice_code)}` : ""}
                          </p>
                        )}
                        {txn.remarks && (
                          <p className="m-0 fs-14 fw-500 mb-2" style={{ color: "#1F2937", lineHeight: "1.4" }}>
                            {sanitizeInput(txn.remarks)}
                          </p>
                        )}
                        <small className="text-muted d-block mb-1">
                          {sanitizeInput(txn.date ?? "-")}
                          {txn.time ? ` at ${sanitizeInput(txn.time)}` : ""}
                          {txn.created_by ? ` by ${sanitizeInput(txn.created_by)}` : ""}
                          {txn.exchange_rate ? ` · Rate: ${sanitizeInput(String(txn.exchange_rate))}` : ""}
                        </small>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentCreate;
